import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  Button,
  Card,
  CardHeader,
  Text,
  Title1,
  Title2,
  Title3,
  Badge,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Divider,
  tokens,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
  Textarea,
  Input,
} from "@fluentui/react-components";
import {
  ArrowLeft24Regular,
  Person24Regular,
  Calendar24Regular,
  Money24Regular,
  Clock20Regular,
  DismissCircle20Regular,
  CheckmarkCircle20Regular,
  Checkmark24Regular,
  Dismiss24Regular,
} from "@fluentui/react-icons";
import { useApiClient } from "../hooks/useApiClient";
import { ApprovalDetails } from "../types/api";
import OrderInformation from "../components/OrderInformation";
import ScenarioTestingControls from "../components/ScenarioTestingControls";

const useStyles = makeStyles({
  container: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  backButton: {
    marginRight: "8px",
  },
  statusBadge: {
    marginLeft: "auto",
  },
  badge: {
    padding: tokens.spacingHorizontalS,
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "24px",
  },
  detailCard: {
    padding: "0", // Remove padding from card itself
    overflow: "hidden", // Prevent overflow
  },
  cardHeader: {
    padding: "16px 16px 0 16px", // Add padding to card headers
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 0",
    minWidth: "0", // Allow content to shrink
  },
  detailLabel: {
    fontWeight: "600",
    color: "#6b7280",
    minWidth: "0", // Allow text to wrap
    marginRight: "8px",
  },
  detailValue: {
    fontWeight: "500",
    minWidth: "0", // Allow text to wrap
    textAlign: "right",
    wordBreak: "break-word", // Allow long text to wrap
  },
  orderPositionsSection: {
    marginTop: "24px",
  },
  financialSection: {
    marginTop: "24px",
  },
  financialGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "24px",
    marginBottom: "24px",
  },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
  },
  cardIcon: {
    color: tokens.colorBrandForeground1,
  },
  cardContent: {
    padding: "12px 16px", // Add consistent padding to card content
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "400px",
  },
  errorContainer: {
    margin: "24px 0",
  },
  actionSection: {
    display: "flex",
    gap: "12px",
    padding: "16px",
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    alignItems: "center",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    marginLeft: "auto",
  },
});

interface ApprovalDetailsPageProps {
  /** Set when opened from an email's Approve/Reject button. Nothing happens
   * without the approver's confirmation: mail scanners pre-open links. */
  intent?: "approve" | "reject";
}

const ApprovalDetailsPage: React.FC<ApprovalDetailsPageProps> = ({ intent }) => {
  const styles = useStyles();
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.adminReturnTo;
  const fromMyRequests = location.state?.myRequestsReturn === true;
  const fromAdmin = typeof returnTo === "string" && (returnTo === "/admin" || returnTo.startsWith("/admin?"));
  const handleBack = () => {
    if (fromAdmin) navigate(returnTo, { replace: true });
    else if (fromMyRequests) navigate("/my-requests", { replace: true });
    else navigate(-1);
  };
  const {
    getApprovalDetails,
    getCurrentUser,
    approveRequest,
    rejectRequest,
    getCFROIData,
    getAdminSettings,
  } = useApiClient();

  const [details, setDetails] = useState<ApprovalDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [globalInterestRate, setGlobalInterestRate] = useState<number>(0.15);
  const [processingAction, setProcessingAction] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Scenario testing state
  const [recalculating, setRecalculating] = useState(false);
  const [isUsingScenario, setIsUsingScenario] = useState(false);

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectNotes, setRejectNotes] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      if (!requestId) {
        setError("No request ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch approval details, current user, and global settings in parallel
        const [approvalDetails, currentUser] = await Promise.all([
          getApprovalDetails(requestId),
          getCurrentUser(),
        ]);

        setDetails(approvalDetails);
        setIsAdmin(currentUser.isAdmin);
        if (currentUser.isAdmin) {
          const settings = await getAdminSettings();
          setGlobalInterestRate(settings.interestRate);
        }
      } catch (err) {
        console.error("Error fetching approval details:", err);
        setError(
          err instanceof Error ? err.message : "Failed to load approval details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [requestId, getApprovalDetails, getCurrentUser, getAdminSettings]);

  // Email "Reject" link: open the dialog once we know the user may reject.
  const [intentHandled, setIntentHandled] = useState(false);
  useEffect(() => {
    if (intent === "reject" && !intentHandled && details?.canApprove) {
      setIntentHandled(true);
      setRejectDialogOpen(true);
    }
  }, [intent, intentHandled, details]);

  // Handle scenario testing recalculation
  const handleScenarioRecalculate = async (
    interestRate: number,
    shippingTime: number,
    paymentTerms: number
  ) => {
    if (!details?.orderNumber || !details?.orderType) {
      console.error("Missing required order details for recalculation");
      return;
    }

    setRecalculating(true);
    setError(null);

    try {
      console.log("🔧 Scenario Testing Parameters:", {
        interestRate,
        interestRatePercentage: (interestRate * 100).toFixed(2) + "%",
        shippingTime,
        paymentTerms,
        orderNumber: details.orderNumber,
        orderType: details.orderType,
      });

      // Call CFROI API with scenario parameters
      // Note: interestRate is already in decimal format (e.g., 0.15 for 15%) from the slider
      const cfroiData = await getCFROIData(details.orderNumber, {
        orderType: details.orderType,
        paymentTerms: paymentTerms,
        interestRate: interestRate, // Already in decimal format from slider
        shippingTime: shippingTime,
      });

      console.log("✅ CFROI Recalculation Result:", {
        portfolioCfroi: cfroiData.portfolioCfroi,
        portfolioCfroiPercentage:
          ((cfroiData.portfolioCfroi || 0) * 100).toFixed(2) + "%",
        skuCount: cfroiData.skuPaymentSeries?.length || 0,
      });

      // Update details with scenario results
      setDetails({
        ...details,
        skuPaymentSeries: cfroiData.skuPaymentSeries,
        portfolioCfroi: cfroiData.portfolioCfroi,
      });

      setIsUsingScenario(true);
    } catch (err: any) {
      console.error("Failed to recalculate CFROI:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to recalculate CFROI with scenario parameters"
      );
    } finally {
      setRecalculating(false);
    }
  };

  // Handle reset to defaults - recalculate without override parameters
  const handleResetToDefaults = async () => {
    if (!details?.orderNumber || !details?.orderType) {
      console.error("Missing required order details for recalculation");
      return;
    }

    setRecalculating(true);
    setError(null);

    try {
      // Use stored payment terms or default based on order type
      const defaultPaymentTerms =
        details.paymentTerms !== undefined ? details.paymentTerms : (details.orderType === "FOB" ? 30 : 90);

      console.log("🔄 Resetting to default parameters:", {
        orderNumber: details.orderNumber,
        orderType: details.orderType,
        paymentTerms: defaultPaymentTerms,
        note: "No override parameters sent - using system defaults",
      });

      // Call CFROI API WITHOUT override parameters
      const cfroiData = await getCFROIData(details.orderNumber, {
        orderType: details.orderType,
        paymentTerms: defaultPaymentTerms,
        // Do NOT include interestRate or shippingTime - let backend use defaults
      });

      console.log("✅ Reset to Defaults Result:", {
        portfolioCfroi: cfroiData.portfolioCfroi,
        portfolioCfroiPercentage:
          ((cfroiData.portfolioCfroi || 0) * 100).toFixed(2) + "%",
        skuCount: cfroiData.skuPaymentSeries?.length || 0,
      });

      // Update details with default results
      setDetails({
        ...details,
        skuPaymentSeries: cfroiData.skuPaymentSeries,
        portfolioCfroi: cfroiData.portfolioCfroi,
      });

      setIsUsingScenario(false); // No longer using custom scenario
    } catch (err: any) {
      console.error("Failed to reset to defaults:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to reset CFROI to default parameters"
      );
    } finally {
      setRecalculating(false);
    }
  };

  const handleApprove = async () => {
    if (
      !requestId ||
      !confirm("Are you sure you want to approve this request?")
    ) {
      return;
    }

    setProcessingAction(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await approveRequest(requestId);
      setSuccessMessage(result.message || "Request approved successfully");

      // Reload details to show updated status
      const updatedDetails = await getApprovalDetails(requestId);
      setDetails(updatedDetails);
    } catch (err: any) {
      console.error("Failed to approve request:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to approve request"
      );
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
    setRejectReason("");
    setRejectNotes("");
  };

  const handleRejectConfirm = async () => {
    if (!requestId || !rejectReason.trim()) {
      setError("Please provide a reason for rejection");
      return;
    }

    setProcessingAction(true);
    setError(null);
    setSuccessMessage(null);
    setRejectDialogOpen(false);

    try {
      const result = await rejectRequest(
        requestId,
        rejectReason,
        rejectNotes || undefined
      );
      setSuccessMessage(result.message || "Request rejected successfully");

      // Reload details to show updated status
      const updatedDetails = await getApprovalDetails(requestId);
      setDetails(updatedDetails);
    } catch (err: any) {
      console.error("Failed to reject request:", err);
      setError(
        err.response?.data?.error || err.message || "Failed to reject request"
      );
    } finally {
      setProcessingAction(false);
      setRejectReason("");
      setRejectNotes("");
    }
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setRejectReason("");
    setRejectNotes("");
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
        return (
          <Badge
            appearance="filled"
            color="success"
            icon={<CheckmarkCircle20Regular />}
            className={styles.badge}
          >
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge
            appearance="filled"
            color="danger"
            icon={<DismissCircle20Regular />}
            className={styles.badge}
          >
            Rejected
          </Badge>
        );
      case "expired":
        return (
          <Badge
            appearance="filled"
            color="warning"
            icon={<Clock20Regular />}
            className={styles.badge}
          >
            Expired
          </Badge>
        );
      default:
        return (
          <Badge
            appearance="filled"
            color="brand"
            icon={<Clock20Regular />}
            className={styles.badge}
          >
            Pending
          </Badge>
        );
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("de-DE", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(dateString));
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner label="Loading approval details..." size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        </div>
        <Button
          appearance="secondary"
          icon={<ArrowLeft24Regular />}
          onClick={handleBack}
        >
          {fromAdmin ? "Back to admin overview" : fromMyRequests ? "Back to my requests" : "Go Back"}
        </Button>
      </div>
    );
  }

  if (!details) {
    return (
      <div className={styles.container}>
        <Text>No approval details found.</Text>
        <Button
          appearance="secondary"
          icon={<ArrowLeft24Regular />}
          onClick={handleBack}
        >
          {fromAdmin ? "Back to admin overview" : fromMyRequests ? "Back to my requests" : "Go Back"}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Success Message */}
      {successMessage && (
        <MessageBar intent="success">
          <MessageBarBody>
            <MessageBarTitle>Success</MessageBarTitle>
            {successMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Error Message */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Header */}
      <div className={styles.header}>
        <Button
          appearance="subtle"
          icon={<ArrowLeft24Regular />}
          onClick={handleBack}
          className={styles.backButton}
        >
          {fromAdmin ? "Back to admin overview" : fromMyRequests ? "Back to my requests" : "Back"}
        </Button>
        <Title1>Approval Request Details</Title1>
        <div className={styles.statusBadge}>
          {getStatusBadge(details.status)}
        </div>
      </div>

      {/* Admin Action Section */}
      {details.requestNotes && (
        <Card>
          <Title3>Purchaser notes</Title3>
          <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{details.requestNotes}</Text>
        </Card>
      )}
      {intent && !details.canApprove && !successMessage && (
        <MessageBar intent="info">
          <MessageBarBody>
            {["pending", "awaiting_second_approval"].includes(details.status.toLowerCase())
              ? "You are not an approver for this request at its current stage."
              : `This request has already been ${details.status.toLowerCase().replace(/_/g, " ")}.`}
          </MessageBarBody>
        </MessageBar>
      )}
      {intent === "approve" && details.canApprove && (
        <MessageBar intent="info">
          <MessageBarBody>Review the request below, then confirm with the Approve button.</MessageBarBody>
        </MessageBar>
      )}
      {details.canApprove && (
        <div className={styles.actionSection}>
          <Text weight="semibold">Approver Actions:</Text>
          <div className={styles.actionButtons}>
            <Button
              appearance="primary"
              icon={<Checkmark24Regular />}
              onClick={handleApprove}
              disabled={processingAction}
            >
              {processingAction
                ? "Processing..."
                : details.status.toLowerCase() === "awaiting_second_approval"
                  ? "Final Approve"
                  : "Approve Request"}
            </Button>
            <Button
              appearance="secondary"
              icon={<Dismiss24Regular />}
              onClick={handleRejectClick}
              disabled={processingAction}
            >
              Reject Request
            </Button>
          </div>
        </div>
      )}

      {/* Scenario Testing Section - Only for admins viewing pending requests */}
      {isAdmin && details.status.toLowerCase() === "pending" && (
        <>
          {isUsingScenario && (
            <MessageBar intent="warning">
              <MessageBarBody>
                <MessageBarTitle>Viewing Scenario Results</MessageBarTitle>
                You are viewing CFROI calculations with custom parameters. This
                does not affect the saved approval data. Reload the page to see
                original values.
              </MessageBarBody>
            </MessageBar>
          )}
          <ScenarioTestingControls
            defaultInterestRate={globalInterestRate}
            defaultShippingTime={details.shippingTime ?? 90}
            defaultPaymentTerms={details.paymentTerms ?? (details.orderType === "FOB" ? 30 : 90)}
            onRecalculate={handleScenarioRecalculate}
            onResetToDefaults={handleResetToDefaults}
            isCalculating={recalculating}
          />
        </>
      )}

      {/* Request Information */}
      <div className={styles.detailsGrid}>
        <Card className={styles.detailCard}>
          <CardHeader
            className={styles.cardHeader}
            header={<Title2>Request Information</Title2>}
            action={<Person24Regular />}
          />
          <div className={styles.cardContent}>
            <div className={styles.detailRow}>
              <Text className={styles.detailLabel}>Order Number:</Text>
              <Text className={styles.detailValue}>{details.orderNumber}</Text>
            </div>
            <div className={styles.detailRow}>
              <Text className={styles.detailLabel}>Requested By:</Text>
              <Text className={styles.detailValue}>
                {details.requestedByName || details.requestedBy}
              </Text>
            </div>
            <div className={styles.detailRow}>
              <Text className={styles.detailLabel}>Email:</Text>
              <Text className={styles.detailValue}>{details.requestedBy}</Text>
            </div>
          </div>
        </Card>

        <Card className={styles.detailCard}>
          <CardHeader
            className={styles.cardHeader}
            header={<Title2>Financial Information</Title2>}
            action={<Money24Regular />}
          />
          <div className={styles.cardContent}>
            {details.portfolioCfroi && (
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Portfolio CFROI:</Text>
                <Text className={styles.detailValue}>
                  {(details.portfolioCfroi * 100).toFixed(2)}%
                </Text>
              </div>
            )}
            {details.totalInvestment && (
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Total Investment:</Text>
                <Text className={styles.detailValue}>
                  {formatCurrency(details.totalInvestment)}
                </Text>
              </div>
            )}
          </div>
        </Card>

        <Card className={styles.detailCard}>
          <CardHeader
            className={styles.cardHeader}
            header={<Title2>Timeline</Title2>}
            action={<Calendar24Regular />}
          />
          <div className={styles.cardContent}>
            <div className={styles.detailRow}>
              <Text className={styles.detailLabel}>Created:</Text>
              <Text className={styles.detailValue}>
                {formatDate(details.createdAt)}
              </Text>
            </div>
            <div className={styles.detailRow}>
              <Text className={styles.detailLabel}>Expires:</Text>
              <Text className={styles.detailValue}>
                {formatDate(details.expiresAt)}
              </Text>
            </div>
            {details.processedAt && (
              <div className={styles.detailRow}>
                <Text className={styles.detailLabel}>Processed:</Text>
                <Text className={styles.detailValue}>
                  {formatDate(details.processedAt)}
                </Text>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Approval/Rejection Information */}
      {(details.approvedBy || details.rejectedBy) && (
        <Card className={styles.detailCard}>
          <CardHeader
            className={styles.cardHeader}
            header={<Title2>Decision Information</Title2>}
          />
          <div className={styles.cardContent}>
            {details.approvedBy && (
              <>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>Approved By:</Text>
                  <Text className={styles.detailValue}>
                    {details.approvedByName || details.approvedBy}
                  </Text>
                </div>
                {details.approvalNotes && (
                  <div className={styles.detailRow}>
                    <Text className={styles.detailLabel}>Notes:</Text>
                    <Text className={styles.detailValue}>
                      {details.approvalNotes}
                    </Text>
                  </div>
                )}
              </>
            )}
            {details.rejectedBy && (
              <>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>Rejected By:</Text>
                  <Text className={styles.detailValue}>
                    {details.rejectedByName || details.rejectedBy}
                  </Text>
                </div>
                {details.rejectionReason && (
                  <div className={styles.detailRow}>
                    <Text className={styles.detailLabel}>Reason:</Text>
                    <Text className={styles.detailValue}>
                      {details.rejectionReason}
                    </Text>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      )}

      {/* Order Information */}
      {((details.orderPositions && details.orderPositions.length > 0) ||
        (details.skuPaymentSeries && details.skuPaymentSeries.length > 0)) && (
        <OrderInformation
          approvalDetails={details}
          paymentTerms={details.paymentTerms}
          title="Order Details"
          showCharts={!!details.skuPaymentSeries}
          showSkuInteraction={!!details.skuPaymentSeries}
        />
      )}

      {/* Financial Analysis */}
      {details.portfolioCfroi && details.totalInvestment && (
        <div className={styles.financialSection}>
          <Title3>Financial Analysis</Title3>
          <Divider />
          <div className={styles.financialGrid}>
            <Card className={styles.detailCard}>
              <CardHeader className={styles.cardHeader}>
                <Text className={styles.cardTitle}>
                  <Money24Regular className={styles.cardIcon} />
                  Portfolio Metrics
                </Text>
              </CardHeader>
              <div className={styles.cardContent}>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>Portfolio CFROI:</Text>
                  <Text className={styles.detailValue}>
                    {(details.portfolioCfroi * 100).toFixed(2)}%
                  </Text>
                </div>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>Total Investment:</Text>
                  <Text className={styles.detailValue}>
                    €{details.totalInvestment.toLocaleString("de-DE")}
                  </Text>
                </div>
              </div>
            </Card>

            <Card className={styles.detailCard}>
              <CardHeader className={styles.cardHeader}>
                <Text className={styles.cardTitle}>
                  <Calendar24Regular className={styles.cardIcon} />
                  Investment Overview
                </Text>
              </CardHeader>
              <div className={styles.cardContent}>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>Items Count:</Text>
                  <Text className={styles.detailValue}>
                    {details.orderPositions?.length || 0} SKUs
                  </Text>
                </div>
                <div className={styles.detailRow}>
                  <Text className={styles.detailLabel}>
                    Avg. Investment per SKU:
                  </Text>
                  <Text className={styles.detailValue}>
                    €
                    {(
                      (details.totalInvestment || 0) /
                      Math.max(details.orderPositions?.length || 1, 1)
                    ).toLocaleString("de-DE")}
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Reject Approval Request</DialogTitle>
            <DialogContent>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <Field label="Reason for Rejection" required>
                  <Input
                    value={rejectReason}
                    onChange={(_, data) => setRejectReason(data.value)}
                    placeholder="e.g., Insufficient budget, Poor CFROI"
                  />
                </Field>
                <Field label="Additional Notes (Optional)">
                  <Textarea
                    value={rejectNotes}
                    onChange={(_, data) => setRejectNotes(data.value)}
                    placeholder="Add any additional context or instructions..."
                    rows={4}
                  />
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleRejectCancel}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim()}
              >
                Confirm Rejection
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ApprovalDetailsPage;
